/**
 * Step 6.5 — Custom Code: Part Number & Noun/Modifier cleanup.
 *
 * Runs after AI extraction. Any extracted value that exactly matches the row's
 * Part Number(s) or Noun/Modifier is removed; every other value is kept unchanged.
 *
 * Exact match = the whole value (case-insensitive, surrounding spaces ignored).
 * Multi-value fields joined with " / " are checked part by part, so only the
 * matching part is removed. Identity fields that are meant to hold these values
 * (NOUN/MODIFIER, part number fields, UNMAPPED) are left alone.
 */

const IDENTITY_FIELD_RE = /^(NOUN[\s/_]*MODIFIER|UNMAPPED|UNMATCHED|(MANUFACTURER|VENDOR|SUPPLIER)[\s_]+PART[\s_]*NUMBER([\s_]*\d+)?)$/i;
const MULTI_VALUE_SEPARATOR = ' / ';

const normalize = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toUpperCase();

/**
 * Values that count as a match.
 * Noun/Modifier: the full value, plus its parts split on "," and "/" (as the
 * earlier Noun/Modifier cleanup did), e.g. "BEARING, BALL" → BEARING, BALL.
 */
export function buildMatchTerms({ partNumbers = [], nounModifier = '' } = {}) {
    const pns = [...new Set(partNumbers.map(normalize).filter(Boolean))];
    const nm = normalize(nounModifier);
    const nmTerms = nm
        ? [...new Set([nm, ...nm.split(/[,/]/).map(part => part.trim()).filter(Boolean)])]
        : [];
    return { partNumbers: pns, nounModifier: nmTerms };
}

/**
 * @param {Object} extractedData - Attributes from AI extraction
 * @param {{ partNumbers: string[], nounModifier: string }} input
 * @returns {{ data: Object, removed: Array<{ field: string, value: string, matched: string }>, terms: Object }}
 */
export function removePartNumberAndNounModifier(extractedData, input) {
    const terms = buildMatchTerms(input);
    if (!extractedData || typeof extractedData !== 'object') {
        return { data: extractedData, removed: [], terms };
    }

    const data = { ...extractedData };
    const removed = [];
    for (const [field, value] of Object.entries(data)) {
        if (typeof value !== 'string' || !value.trim() || IDENTITY_FIELD_RE.test(field.trim())) continue;

        const parts = value.split(MULTI_VALUE_SEPARATOR);
        const kept = parts.filter(part => {
            const p = normalize(part);
            const matched = terms.partNumbers.includes(p) ? 'Part Number'
                : terms.nounModifier.includes(p) ? 'Noun/Modifier'
                    : null;
            if (matched) removed.push({ field, value: part.trim(), matched });
            return !matched;
        });
        if (kept.length !== parts.length) {
            data[field] = kept.length > 0 ? kept.join(MULTI_VALUE_SEPARATOR) : null;
        }
    }
    return { data, removed, terms };
}
